// @flow
import React, { useRef, useEffect, useState } from 'react';
import Debug from 'debug';
import OlVectorSource from 'ol/source/Vector';
import { Stroke, Style } from 'ol/style';
import type { ShapeType, Shape } from '../../../../types';
import { ZINDEX } from '../../../../constants/appConstants';
import { interactions, layers } from '../../../ui/openlayers';
import * as utils from '../../../../utils/zoneUtils';
import { makeEditableStyle } from '../style';

const debug = Debug('amms:modules:sidebar:zones:drawing');

const style = makeEditableStyle()(
    new Style({
        stroke: new Stroke({
            color: '#aa7a27',
            width: 3
        })
    })
);

export type Props = $ReadOnly<{
    drawingEnabled: boolean,
    type: ShapeType,
    shape: Shape,
    newZone: boolean,

    setShape: (shape: Shape) => void
}>;

function Drawing({ drawingEnabled, type, shape, newZone, setShape }: Props) {
    const [source, setSource] = useState();
    const drawingRef = useRef();

    useEffect(() => {
        setSource(drawingEnabled ? new OlVectorSource() : null);
    }, [drawingEnabled]);

    useEffect(() => {
        if(drawingEnabled && source) {
            if(drawingRef.current) {
                const feature = drawingRef.current.feature;
                const finished = drawingRef.current.finished;
                const geometry = feature.getGeometry();

                if(geometry.getType() !== utils.getType(type)) {
                    if(source.hasFeature(feature)) {
                        source.removeFeature(feature);
                    }

                    const newFeature = utils.createFeature(type, shape);
                    source.addFeature(newFeature);
                    drawingRef.current = {
                        feature: newFeature,
                        finished: false
                    };
                }
                else {
                    const featureShape = utils.getShape(feature, type, finished);

                    if(!utils.isShapeEqual(featureShape, shape, type)) {
                        feature.setGeometry(utils.createGeometry(type, shape));
                    }
                }
            }
            else {
                const newFeature = utils.createFeature(type, shape);
                source.addFeature(newFeature);
                drawingRef.current = {
                    feature: newFeature,
                    finished: false
                };
            }
        }
        else {
            drawingRef.current = null;
        }
    }, [drawingEnabled, source, type, shape]);

    if(!drawingEnabled || !source) {
        return null;
    }

    const onDrawStart = (e) => {
        debug('Draw start. Feature: ', e.feature);

        if(drawingRef.current) {
            if(source.hasFeature(drawingRef.current.feature)) {
                source.removeFeature(drawingRef.current.feature);
            }
        }

        drawingRef.current = {
            feature: e.feature,
            finished: false
        };
        setShape(utils.getShape(e.feature, type));
    };

    const onDraw = (e) => {
        debug('Draw. Feature: ', e.feature);
        setShape(utils.getShape(e.feature, type));
    };

    const onDrawEnd = (e) => {
        debug('Draw end. Feature: ', e.feature);
        drawingRef.current.finished = true;
        setShape(utils.getShape(e.feature, type, true));
    };

    const onModifyEnd = (e) => {
        debug('Modify end. Features: ',e.features);

        const feature = e.features.item(0);
        setShape(utils.getShape(feature, type, true));
    };

    return (
        <layers.Vector label="drawing" source={source} style={style} zIndex={ZINDEX.drawing}>
            {newZone &&
                <interactions.Draw
                    type={utils.getType(type)}
                    onDrawStart={onDrawStart}
                    onDraw={onDraw}
                    onDrawEnd={onDrawEnd}
                />
            }

            <interactions.Modify onModifyEnd={onModifyEnd} />
        </layers.Vector>
    );
}

export default Drawing;